import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { isDone } from "../components/FollowUpSlot";
import { STAGES } from "../lib/constants";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { PriorityBadge } from "../components/Badges";
import { LeadFormDialog } from "../components/LeadFormDialog";
import { LeadDrawer } from "../components/LeadDrawer";

const LeadCard = ({ l, onOpen }) => (
  <div draggable onDragStart={(e) => e.dataTransfer.setData("id", l.id)} onClick={() => onOpen(l.id)} data-testid={`kanban-card-${l.id}`}
    className="cursor-grab rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:border-emerald-300 hover:shadow transition-[border-color,box-shadow]">
    <div className="flex items-start justify-between gap-2">
      <div className="font-semibold text-sm leading-tight">{l.name}</div>
      <PriorityBadge p={l.priority} />
    </div>
    <div className="mt-1 text-xs text-slate-500 truncate">{l.company}</div>
    <div className="mt-3 flex items-center justify-between">
      <span className="text-xs font-semibold text-emerald-700">{l.interested_for || "-"}</span>
      <span className="text-[11px] text-slate-400">{l.followups.filter(isDone).length}/{l.followups.length} FU</span>
    </div>
  </div>
);

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [over, setOver] = useState(null);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const drop = async (e, stage) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData("id");
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.status === stage) return;
    setLeads((p) => p.map((l) => (l.id === id ? { ...l, status: stage } : l)));
    try { await api.patch(`/leads/${id}/status`, { status: stage }); toast.success(`Moved to ${stage}`); }
    catch (err) { toast.error(errMsg(err)); load(); }
  };

  return (
    <div data-testid="pipeline-page">
      <PageHeader title="Pipeline" subtitle="Drag leads across stages to update their progress.">
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)} data-testid="pipeline-add-lead-btn"><Plus className="h-4 w-4 mr-1" />New lead</Button>
      </PageHeader>
      <div className="flex gap-4 overflow-x-auto pb-4 scroll-thin">
        {STAGES.map((s) => {
          const items = leads.filter((l) => l.status === s.key);
          return (
            <div key={s.key} data-testid={`kanban-col-${s.key}`} onDragOver={(e) => { e.preventDefault(); setOver(s.key); }} onDragLeave={() => setOver(null)} onDrop={(e) => drop(e, s.key)}
              className={`kanban-col w-72 shrink-0 rounded-xl bg-slate-100/70 p-3 transition-colors ${over === s.key ? "drag-over" : ""}`}>
              <div className="mb-3 flex items-center justify-between px-1">
                <div className="flex items-center gap-2 text-sm font-bold"><span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />{s.label}<span className="text-slate-400 font-medium">{items.length}</span></div>
                
              </div>
              <div className="space-y-2 min-h-[120px]">{items.map((l) => <LeadCard key={l.id} l={l} onOpen={setSel} />)}</div>
            </div>
          );
        })}
      </div>
      <LeadFormDialog open={open} onOpenChange={setOpen} onSaved={load} />
      <LeadDrawer leadId={sel} onClose={() => setSel(null)} onChanged={load} />
    </div>
  );
}
